import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { format } from "date-fns";
import { Video, Calendar, Filter, X } from "lucide-react";
import { cn } from "~/lib/cn";
import { Button } from "~/components/ui/button";
import { Badge } from "~/components/ui/badge";
import { SummitSessionCard } from "~/components/summit";
import type { SessionFooterVariant } from "~/lib/shared/types/summit";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getActiveSummitDataOptions,
  registerForSession,
  unregisterFromSession,
} from "~/lib/functions/summit";
import { getUserPasswordStatus } from "~/lib/functions/user-security";
import type { SummitSession, SummitSpeaker } from "~/lib/shared/types/summit";
import { getCDNImageUrl } from "~/components/ui/cdn-image";
import { createBrandMeta } from "~/lib/shared/meta";
import {
  SUMMIT_WORKSHOP_REGISTRATION_LIMIT,
  SUMMIT_VIP_UPSELL_URL,
} from "~/lib/shared/constants/summit";
import { z } from "zod";

const AgendaSearchSchema = z
  .object({
    am_id: z.string().optional(),
  })
  .passthrough();

export const Route = createFileRoute("/_user/summit/agenda")({
  validateSearch: (search) => AgendaSearchSchema.parse(search ?? {}),
  loader: async ({ context }) => {
    const summitData = await context.queryClient.ensureQueryData(
      getActiveSummitDataOptions,
    );

    if (!summitData.userRegistration.isRegistered) {
      throw redirect({
        to: "/summit",
      });
    }

    const passwordStatus = await getUserPasswordStatus();

    if (!passwordStatus.hasPassword) {
      const redirectSearch: Record<string, string> = {
        redirectTo: "/summit/agenda",
        message:
          "You need to set a password first to ensure you will keep having access.",
      };

      throw redirect({
        to: "/settings/password",
        search: () => redirectSearch,
        replace: true,
      });
    }

    return summitData;
  },
  head: ({ loaderData }) => {
    const summitData = loaderData;

    if (!summitData) {
      return createBrandMeta({
        title: "Summit - Join Expert Coaching Sessions",
        description:
          "Join industry leaders and master coaches for an immersive learning experience that will transform your coaching practice.",
        ogType: "website",
      });
    }

    // Generate keywords from session topics
    const allTopics = new Set<string>();
    summitData.days.forEach((day) => {
      day.sessions.forEach((session) => {
        (session.topics || []).forEach((topic) => allTopics.add(topic));
      });
    });

    const keywords = [
      "summit",
      "coaching",
      "professional development",
      "master coaches",
      "industry leaders",
      ...Array.from(allTopics),
    ];

    // Create summit description with session count
    const totalSessions = summitData.days.reduce(
      (count, day) => count + day.sessions.length,
      0,
    );
    const description = `Join industry leaders and master coaches for ${summitData.summit.title}. ${totalSessions} sessions covering ${Array.from(allTopics).slice(0, 3).join(", ")} and more. Transform your coaching practice with expert insights.`;

    return createBrandMeta({
      title: summitData.summit.title,
      description,
      ogTitle: `${summitData.summit.title} | Dream Coach Match`,
      ogDescription: description,
      ogImage: getCDNImageUrl(
        "https://form-data.dreamcoachmatch.com/public/summit-bg.png",
        1200,
      ),
      ogType: "website",
      ogUrl: "https://dreamcoachmatch.com/summit/agenda",
      twitterCard: "summary_large_image",
      twitterTitle: summitData.summit.title,
      twitterDescription: description,
      twitterImage: getCDNImageUrl(
        "https://form-data.dreamcoachmatch.com/public/summit-bg.png",
        1200,
      ),
      canonical: "https://dreamcoachmatch.com/summit/agenda",
      keywords,
      author: "Dream Coach Match",
    });
  },
  component: SummitAgendaPage,
});

function SummitAgendaPage() {
  const isAgendaTemporarilyHidden =
    import.meta.env.VITE_SUMMIT_AGENDA_PLACEHOLDER !== "false";

  if (isAgendaTemporarilyHidden) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 px-4 py-24 text-center text-white">
        <div className="max-w-2xl space-y-4">
          <h1 className="font-heading text-4xl tracking-tight sm:text-5xl">
            Summit agenda coming soon
          </h1>
          <p className="text-lg text-purple-100">
            We&rsquo;re finalizing the workshop lineup and will publish the full
            agenda shortly. Check back soon to claim your seat—these slots will
            be filled quickly!
          </p>
        </div>
      </div>
    );
  }

  return <SummitAgendaContent />;
}

function SummitAgendaContent() {
  const queryClient = useQueryClient();
  const {
    data: summitData,
    isLoading,
    error,
  } = useQuery(getActiveSummitDataOptions);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [openDays, setOpenDays] = useState<string[]>([]);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const registerMutation = useMutation({
    mutationFn: registerForSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["summit", "active"] });
      setMutationError(null);
    },
    onError: (error) => {
      const message =
        error instanceof Error
          ? error.message
          : "We couldn't register you for that workshop. Please try again.";
      setMutationError(message);
    },
  });

  const unregisterMutation = useMutation({
    mutationFn: unregisterFromSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["summit", "active"] });
      setMutationError(null);
    },
    onError: (error) => {
      const message =
        error instanceof Error
          ? error.message
          : "We couldn't update your registration right now. Please try again.";
      setMutationError(message);
    },
  });

  // Set first day open by default
  useMemo(() => {
    if (summitData?.days.length && openDays.length === 0) {
      setOpenDays([summitData.days[0].id]);
    }
  }, [summitData?.days, openDays.length]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-gray-600">Loading summit...</div>
      </div>
    );
  }

  if (error || !summitData) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-red-600">Error loading summit data</div>
      </div>
    );
  }

  // Convert database format to component format
  const convertSession = (
    session: (typeof summitData.days)[0]["sessions"][0],
  ): SummitSession => {
    return {
      id: session.id,
      title: session.title,
      description: session.description,
      fullDescription: session.fullDescription || undefined,
      speaker: session.speaker as SummitSpeaker,
      date: new Date(session.date),
      startTime: session.startTime,
      endTime: session.endTime,
      timezone: "PST",
      topics: session.topics || [],
      recordingUrl: session.recordingUrl || undefined,
      isVipOnly: session.isVipOnly,
      sessionFormat: session.sessionFormat as SummitSession["sessionFormat"],
    };
  };

  const getFooterVariant = (
    session: (typeof summitData.days)[0]["sessions"][0],
  ): SessionFooterVariant => {
    // Use footerVariants from server if available, otherwise create default
    return summitData?.footerVariants?.[session.id] || { type: "no-cta" };
  };

  // Get all unique tags from all days
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    summitData?.days.forEach((day) => {
      day.sessions.forEach((session) => {
        (session.topics || []).forEach((topic) => tags.add(topic));
      });
    });
    return Array.from(tags).sort();
  }, [summitData?.days]);

  // Filter sessions based on selected tags
  const filteredDays = useMemo(() => {
    if (!summitData) return [];
    if (selectedTags.length === 0) return summitData.days;

    return summitData.days
      .map((day) => ({
        ...day,
        sessions: day.sessions.filter((session) =>
          (session.topics || []).some((topic) => selectedTags.includes(topic)),
        ),
      }))
      .filter((day) => day.sessions.length > 0);
  }, [selectedTags, summitData]);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const clearFilters = () => {
    setSelectedTags([]);
  };

  const handleAddToCalendar = (day: (typeof summitData.days)[0]) => {
    // Create calendar event details
    const startDate = format(new Date(day.date), "yyyyMMdd");
    const title = encodeURIComponent(`Summit: ${day.title}`);
    const details = encodeURIComponent(`Join via Zoom: ${day.zoomUrl}`);

    // Google Calendar URL
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${startDate}&details=${details}`;

    window.open(googleCalendarUrl, "_blank");
  };

  const handleRegister = async (sessionId: string) => {
    await registerMutation.mutateAsync({ data: { sessionId } });
  };

  const handleCancelRegistration = async (sessionId: string) => {
    await unregisterMutation.mutateAsync({ data: { sessionId } });
  };

  const isVip = summitData?.userRegistration?.isVip ?? false;
  const workshopRegistrationsCount =
    summitData?.userRegistration?.workshopRegistrationsCount ?? 0;
  const workshopLimit =
    summitData?.userRegistration?.workshopRegistrationLimit ??
    SUMMIT_WORKSHOP_REGISTRATION_LIMIT;
  const vipUpsellUrl = summitData.vipUpsellUrl ?? SUMMIT_VIP_UPSELL_URL;

  return (
    <div className="min-h-screen px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="mb-4 font-heading text-4xl tracking-tight text-gray-900 sm:text-5xl">
            {summitData.summit.title}
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-gray-600">
            Join industry leaders and master coaches for an immersive learning
            experience that will transform your coaching practice.
          </p>
        </div>

        {/* Summit Container with Background */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 p-8">
          {/* Content */}
          <div className="relative z-10">
            <div className="mb-6 space-y-4 rounded-lg border border-white/20 bg-black/20 p-4 text-sm text-purple-50 backdrop-blur">
              {isVip ? (
                <div>
                  <p className="font-medium text-white">
                    VIP Workshops Access Enabled
                  </p>
                  <p className="mt-1 text-purple-100">
                    You're registered for {workshopRegistrationsCount}/
                    {workshopLimit} workshops. Choose up to {workshopLimit} in
                    total.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-medium text-white">
                      Unlock VIP-Only Workshops
                    </p>
                    <p className="mt-1 text-purple-100">
                      Upgrade to VIP to claim your seat in up to {workshopLimit}{" "}
                      interactive workshops and access exclusive resources.
                    </p>
                  </div>
                  <Button
                    variant="orange"
                    size="sm"
                    className="w-full sm:w-auto"
                    onClick={() => window.open(vipUpsellUrl, "_blank")}
                  >
                    Upgrade to VIP
                  </Button>
                </div>
              )}
              {mutationError && (
                <div className="rounded-md border border-red-300 bg-red-900/40 p-3 text-red-100">
                  {mutationError}
                </div>
              )}
            </div>

            {/* Filters */}
            {allTags.length > 0 && (
              <div className="mb-8 rounded-lg bg-black/10 border border-white/20 p-4 backdrop-blur-md">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-white">
                    <Filter className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      Filter by Topics
                    </span>
                  </div>
                  {selectedTags.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="h-auto p-1 text-white hover:text-white/80"
                    >
                      Clear all
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {allTags.map((tag) => (
                    <Badge
                      key={tag}
                      variant={
                        selectedTags.includes(tag) ? "default" : "outline"
                      }
                      className={cn(
                        "cursor-pointer transition-all",
                        selectedTags.includes(tag)
                          ? "bg-white text-purple-900 hover:bg-white/90"
                          : "border-white/30 text-white hover:border-white/50 hover:bg-white/10",
                      )}
                      onClick={() => toggleTag(tag)}
                    >
                      {tag}
                      {selectedTags.includes(tag) && (
                        <X className="ml-1 h-3 w-3" />
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Summit Days Accordion */}
            <Accordion.Root
              type="multiple"
              value={openDays}
              onValueChange={setOpenDays}
              className="space-y-4"
            >
              {filteredDays.map((day) => (
                <AccordionItem
                  key={day.id}
                  value={day.id}
                  className="overflow-hidden rounded-lg border-1 border-white/20 pt-6 bg-black/10 backdrop-blur-lg shadow-2xl shadow-brand-800/30"
                >
                  <AccordionTrigger className="px-6 py-4 hover:no-underline">
                    <div className="text-left">
                      <h2 className="font-heading text-4xl tracking-tight text-white">
                        {day.title}
                      </h2>
                      <p className="mt-1 text-sm text-purple-100">
                        {format(new Date(day.date), "EEEE, MMMM d, yyyy")} •{" "}
                        {day.sessions.length} sessions
                      </p>
                      {day.description && (
                        <p className="mt-2 text-sm text-purple-200">
                          {day.description}
                        </p>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    {/* Day Actions */}
                    <div className="mb-6 flex flex-wrap gap-3 border-b border-white/10 pb-4">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => window.open(day.zoomUrl, "_blank")}
                        className="gap-2 bg-white/20 text-white hover:bg-white/30"
                      >
                        <Video className="h-4 w-4" />
                        Join Zoom Room
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleAddToCalendar(day)}
                        className="gap-2 bg-white/20 text-white hover:bg-white/30"
                      >
                        <Calendar className="h-4 w-4" />
                        Add to Calendar
                      </Button>
                    </div>

                    {/* Session Cards Grid */}
                    <div className="space-y-4">
                      {day.sessions.map((session) => (
                        <SummitSessionCard
                          key={session.id}
                          session={convertSession(session)}
                          summitId={summitData.summit.id}
                          footerVariant={getFooterVariant(session)}
                          userIsVip={
                            summitData?.userRegistration?.isVip || false
                          }
                          vipUpsellUrl={vipUpsellUrl}
                          onRegister={() => handleRegister(session.id)}
                          onCancelRegistration={() =>
                            handleCancelRegistration(session.id)
                          }
                        />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion.Root>

            {filteredDays.length === 0 && (
              <div className="rounded-lg bg-white/10 p-12 text-center backdrop-blur-md">
                <p className="text-lg text-white">
                  No sessions match your selected filters.
                </p>
                <Button
                  variant="secondary"
                  onClick={clearFilters}
                  className="mt-4 bg-white/20 text-white hover:bg-white/30"
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
